/**
 * @file mouse-keys.ts
 * @description Telink 3228C 鼠标按键区的 A7/A8 读取、写入与 Web contract 转换。
 */

import { mouseKeyFeature } from '../../../../features/mouse-keys/contract'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import type {
  keyAttr,
  keyMapItem,
  LayerKeysMap,
  macro,
  mouseKey,
} from '../../../../types/mouseKeys'
import type { Action, Macro } from '../../../../types/macro'
import type { Telink3228CMouseDevice } from '../../../../devices/mouse/telink-3228c/device'
import {
  assertVisionApProfileIndex,
  decodeVisionApMacroName,
  encodeVisionApMacroName,
  getVisionApMacroNameSlotOffset,
  hasVisionApMacroNameSlot,
  VISION_AP_MOUSE_FUNCTION_AREA,
  VISION_AP_MOUSE_MACRO_NAME_AREA,
} from '../../protocols/vision-ap-mouse'

const KEY_FORMAT_SIZE = 4
const KEY_AREA_BYTE_LENGTH = 128
// 接收器对 56 字节帧会截断功能区后段；按键区也统一使用 DPI 已验证的 24 字节安全颗粒度。
const KEY_AREA_CHUNK_LENGTHS = [24, 24, 24, 24, 24, 8] as const

// KeyFormat 按协议地址顺序保存；中键、前进和后退槽位由实机改键后的物理按键结果确认。
const KEY_SLOT_BY_LAYOUT_INDEX: ReadonlyMap<number, number> = new Map([
  [0, 0],
  [1, 2],
  [2, 1],
  [3, 5],
  [4, 6],
  [5, 4],
  [6, 3],
  [9, 8],
  [10, 9],
  [13, 7],
])

const KEY_ATTR_TYPE = {
  keyboard: 0x00,
  combined: 0x02,
  mouseButton: 0x03,
  media: 0x04,
  macro: 0x05,
  disable: 0x07,
  dpi: 0x08,
  function: 0x09,
  diy: 0x0a,
  verticalWheel: 0x0b,
  horizontalWheel: 0x0c,
  arbitraryCombined: 0x0d,
  default: 0xff,
} as const

const RECEIVER_SWITCH_KEY_FORMAT_TYPE = 0x0f
const RECEIVER_SWITCH_DIY_TYPE = 3
const RECEIVER_SWITCH_OFFSET = VISION_AP_MOUSE_FUNCTION_AREA.receiverSwitchOffset
const REPORT_RATE_KEY_FORMAT_TYPE = 0x06
const REPORT_RATE_DIY_TYPE = 0
const CONFIG_CYCLE_KEY_FORMAT_TYPE = 0x10
const CONFIG_CYCLE_PROTOCOL_CODE = 0x03
const CONFIG_CYCLE_DIY_TYPE = 2

// 页面没有暴露 Fire 的速度参数；VT2 固定使用 10ms 两连发，避免伪造可调能力。
const FIRE_DEFAULT_INTERVAL_MS = 10
const FIRE_DEFAULT_SHOT_COUNT = 2
// 厂商确认滚轮前/后对应矩阵槽 8/9，原始 KeyFormat 均为
// `MS_WHEEL, Code_L=0x00, Code_H=0x01/0xFF`。Code_L 保持厂家确认的默认值。
const WHEEL_CODE_L = 0x00
const MACRO_HEADER_BYTE_LENGTH = 16
const MACRO_ADDRESS_BYTE_LENGTH = 2
const MACRO_DATA_HEADER_BYTE_LENGTH = 4
const MACRO_ACTION_BYTE_LENGTH = 4
// 原厂接收器样本曾以 38 字节 AB 写入，但用户实机（2026-08-17）直发 30 字节 AB 只持久化前 24 字节；
// 宏区与 DPI 区统一使用已验证的 24 字节安全颗粒度，读取与写入都按此分块。
const MACRO_TRANSFER_CHUNK_LENGTH = 24
const MACRO_READ_FALLBACK_LENGTH = 56
const MAX_MACRO_AREA_BYTE_LENGTH = 4096
// 协议宏动作类型；普通键盘与鼠标键已抓包确认，坐标/滚轮按 PDF 表逐项实现。
const MACRO_ACTION_CODE_TYPE = {
  mouseButton: 0x01,
  mouseSwipe: 0x02,
  mouseWheel: 0x03,
  mouseX: 0x04,
  mouseY: 0x05,
  keyboardHot: 0x09,
  keyboardNormal: 0x0a,
} as const
// 协议将坐标方向放在状态字节 bit7，code 字节保存 0~255 的偏移幅度。
const MACRO_COORDINATE_MAX_MAGNITUDE = 0xff
const WEB_COORDINATE_KEY = 0xf7
const WEB_WHEEL_KEY = 0xf8
// 与按键区滚轮方向约定保持一致：0xFF 表示 Web 反向，0x01 表示正向。
const MACRO_WHEEL_POSITIVE = 0x01
const MACRO_WHEEL_NEGATIVE = 0xff

const WEB_MACRO_MOUSE_TO_PROTOCOL: ReadonlyMap<number, number> = new Map([
  [0xf0, 0x01],
  [0xf1, 0x02],
  [0xf2, 0x04],
  [0xf3, 0x08],
  [0xf4, 0x10],
])

const PROTOCOL_MACRO_MOUSE_TO_WEB: ReadonlyMap<number, number> = new Map(
  Array.from(WEB_MACRO_MOUSE_TO_PROTOCOL, ([webCode, protocolCode]) => [protocolCode, webCode]),
)

interface MacroAreaEntry {
  address: number
  end: number
  headerReserved: Uint8Array
  actions?: Action[]
  hasUnmappedActions: boolean
}

interface MacroAreaSnapshot {
  raw: Uint8Array
  entries: MacroAreaEntry[]
}

const SYSTEM_CONTROL_CODES = new Set([0x81, 0x82, 0x83])

const PROTOCOL_MOUSE_TO_WEB_INDEX: ReadonlyMap<number, number> = new Map([
  [0x01, 0],
  [0x02, 1],
  [0x04, 2],
  [0x08, 3], // Button4 / 后退
  [0x10, 4], // Button5 / 前进
])

const WEB_MOUSE_TO_PROTOCOL: ReadonlyMap<number, number> = new Map(
  Array.from(PROTOCOL_MOUSE_TO_WEB_INDEX, ([protocolCode, webIndex]) => [webIndex, protocolCode]),
)

// V0107 + 用户确认按协议表顺序（2026-09-21）：3=DPI+ 循环、4=DPI- 循环；1/2 仍为不循环的 DPI+/-。
const PROTOCOL_DPI_TO_WEB_TYPE: ReadonlyMap<number, 3 | 4 | 5 | 6> = new Map([
  [0x01, 5],
  [0x02, 6],
  [0x03, 3],
  [0x04, 4],
])

const WEB_DPI_TO_PROTOCOL_TYPE: ReadonlyMap<number, 1 | 2 | 3 | 4> = new Map([
  [5, 1],
  [6, 2],
  [3, 3],
  [4, 4],
])

/** VT2 ULTRA V2 的当前按键表适配。宏内容读写仍由协议宏区证据单独覆盖。 */
export class Telink3228CMouseKeys extends mouseKeyFeature {
  /**
   * V0106 宏名称区（B9/BA）的会话内快照：最近一次从设备读回或写后回读确认的 240 字节。
   * 名称只会经 SDK 修改，会话内可复用；恢复出厂后由 device 调用 `invalidateMacroNameArea` 丢弃。
   */
  private macroNameArea?: Uint8Array
  private macroNameAreaInFlight?: Promise<Uint8Array>
  /**
   * Web 沿用 nRF54 的宏级 `Macro.loop` 交互；维盛协议则把播放方式放在按键 KeyFormat。
   * 因此这里只保存本次 SDK 会话中用户选择的“下次绑定偏好”，绝不写入 AA/AB 保留字节。
   */
  private readonly pendingMacroLoopPreferenceCache = new Map<number, number>()

  constructor(private readonly telinkDevice: Telink3228CMouseDevice) {
    super(telinkDevice)
  }

  public async getMouseKeyMap(): Promise<LayerKeysMap> {
    const keyArea = await this.telinkDevice.runKeyAreaSession((session) =>
      this.readKeyAreaSnapshot(session),
    )

    return Array.from(KEY_SLOT_BY_LAYOUT_INDEX, ([index, slot]) => ({
      index,
      keyAttr: this.parseKeyFormat(
        keyArea.slice(slot * KEY_FORMAT_SIZE, (slot + 1) * KEY_FORMAT_SIZE),
      ),
    }))
  }

  public async setMouseKeyMap(map: LayerKeysMap): Promise<void> {
    if (!Array.isArray(map)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '按键映射必须是数组')
    }

    let receiverSwitchFlag: 0 | 1 | undefined
    await this.telinkDevice.runKeyAreaSession(async (session) => {
      const keyArea = await this.readKeyAreaSnapshot(session)
      const hadReceiverSwitch = this.hasReceiverSwitchKey(keyArea)
      let writesReceiverSwitch = false
      for (const item of map) {
        const slot = this.resolveKeySlot(item.index)
        const keyFormat = this.serializeKeyFormat(item.keyAttr)
        writesReceiverSwitch ||= keyFormat[0] === RECEIVER_SWITCH_KEY_FORMAT_TYPE
        keyArea.set(keyFormat, slot * KEY_FORMAT_SIZE)
      }
      const hasReceiverSwitch = this.hasReceiverSwitchKey(keyArea)
      if (writesReceiverSwitch || hadReceiverSwitch !== hasReceiverSwitch) {
        receiverSwitchFlag = hasReceiverSwitch ? 1 : 0
      }
      await this.writeKeyAreaSnapshot(session, keyArea)
    })
    if (receiverSwitchFlag !== undefined) {
      await this.writeReceiverSwitchFlag(receiverSwitchFlag)
    }
    for (const item of map) this.clearPendingMacroLoopPreference(item.keyAttr)
  }

  public async setMouseKey(key: keyMapItem, configIndex: number): Promise<void> {
    assertVisionApProfileIndex(configIndex)
    const slot = this.resolveKeySlot(key.index)
    const data = this.serializeKeyFormat(key.keyAttr)

    let receiverSwitchFlag: 0 | 1 | undefined
    await this.telinkDevice.runKeyAreaSession(async (session) => {
      const keyArea = await this.readKeyAreaSnapshot(session)
      const hadReceiverSwitch = this.hasReceiverSwitchKey(keyArea)
      keyArea.set(data, slot * KEY_FORMAT_SIZE)
      const hasReceiverSwitch = this.hasReceiverSwitchKey(keyArea)
      if (data[0] === RECEIVER_SWITCH_KEY_FORMAT_TYPE || hadReceiverSwitch !== hasReceiverSwitch) {
        receiverSwitchFlag = hasReceiverSwitch ? 1 : 0
      }
      await this.writeKeyAreaSnapshot(session, keyArea)
    }, configIndex)
    if (receiverSwitchFlag !== undefined) {
      await this.writeReceiverSwitchFlag(receiverSwitchFlag, configIndex)
    }
    this.clearPendingMacroLoopPreference(key.keyAttr)
  }

  public async getMacroList(): Promise<Macro[]> {
    // 宏区与名称区是独立会话，不能嵌套 runAtomic。先读 AA 得到连续条目，再读 B9 填名称。
    const entries = await this.telinkDevice.runMacroAreaSession(async (session) => {
      const snapshot = await this.readMacroAreaSnapshot(session)
      return snapshot.entries
    })
    await this.loadMacroNameArea()
    return entries.map((_, index) => this.createMacroSummary(index))
  }

  public async *getMacroListStream(batchSize: number = 3): AsyncIterable<Macro[]> {
    if (!Number.isInteger(batchSize) || batchSize < 1) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏列表批次大小必须为正整数')
    }

    const macros = await this.getMacroList()
    for (let start = 0; start < macros.length; start += batchSize) {
      yield macros.slice(start, start + batchSize)
    }
  }

  public async getMacroByIndex(index: number): Promise<Macro> {
    this.assertMacroIndex(index)
    const content = await this.telinkDevice.runMacroAreaSession(async (session) => {
      const snapshot = await this.readMacroAreaSnapshot(session)
      const entry = snapshot.entries[index]
      if (!entry) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `宏索引 ${index} 不存在`)
      }
      return {
        package: entry.actions,
        ignoreDelay:
          entry.actions !== undefined &&
          entry.actions.length > 0 &&
          entry.actions.every((action) => action.time === 2),
      }
    })
    await this.loadMacroNameArea()
    const loop =
      this.pendingMacroLoopPreferenceCache.get(index) ??
      (await this.readMacroLoopFromCurrentKeyArea(index))
    return { ...this.createMacroSummary(index), ...content, loop }
  }

  public async setMacroByIndex(macro: Macro, index: number): Promise<void> {
    this.assertMacroIndex(index)
    if (!macro || (macro.package !== undefined && !Array.isArray(macro.package))) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏动作必须是数组')
    }

    const loopPreference = this.requireMacroLoop(macro.loop ?? 1)
    let deleted = false
    await this.telinkDevice.runMacroAreaSession(async (session) => {
      const snapshot = await this.readMacroAreaSnapshot(session)
      if (index > snapshot.entries.length) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `宏索引 ${index} 超出连续宏表范围`)
      }
      // 空名称 + 空动作 = 删除操作：从宏区中移除该条目。
      if ((macro.package === undefined || macro.package.length === 0) && !macro.name) {
        const updated = this.removeMacroAreaEntry(snapshot, index)
        await this.writeMacroAreaSnapshot(session, updated)
        deleted = true
        return
      }
      // 页面未建模的动作没有 package；宏区必须保留原始内容，名称仍可单独写入名称区。
      if (macro.package === undefined) {
        return
      }
      // 有名称 + 空动作 = 新建占位：仍写入宏区空条目，使名称槽位与地址表索引对齐。
      const updated = this.replaceMacroAreaEntry(snapshot, macro, index)
      await this.writeMacroAreaSnapshot(session, updated)
    })
    if (deleted) {
      this.pendingMacroLoopPreferenceCache.delete(index)
      // 宏区条目删除后后续宏索引前移，名称槽位必须同步前移，否则名称会错位。
      await this.removeMacroName(index)
      return
    }
    // V0106 起宏名称持久化在 B9/BA 名称区；名称未变化时不产生任何名称区传输。
    if (macro.name) {
      await this.persistMacroName(index, macro.name)
    }

    this.pendingMacroLoopPreferenceCache.set(index, loopPreference)
    if (macro.package !== undefined) {
      const storedInCurrentProfile = await this.updateCurrentProfileMacroBindings(
        index,
        loopPreference,
      )
      if (storedInCurrentProfile) this.pendingMacroLoopPreferenceCache.delete(index)
    }
  }

  /**
   * Web 以宏级 loop 编辑播放方式；V0105 则把它放在每个键位 KeyFormat。
   * 保存宏时只更新当前 profile 中已经绑定该宏的键位，不触碰其它 profile。
   */
  private async updateCurrentProfileMacroBindings(
    macroIndex: number,
    loop: number,
  ): Promise<boolean> {
    const macroKeyFormat = this.serializeMacroKey({ index: macroIndex, loop })
    return this.telinkDevice.runKeyAreaSession(async (session) => {
      const keyArea = await this.readKeyAreaSnapshot(session)
      let changed = false
      let found = false
      for (
        let offset = 0;
        offset + KEY_FORMAT_SIZE <= keyArea.byteLength;
        offset += KEY_FORMAT_SIZE
      ) {
        const type = keyArea[offset]
        const boundMacroIndex = keyArea[offset + 1]
        if ((type !== 0x0b && type !== 0x0c) || boundMacroIndex !== macroIndex) continue
        found = true
        const current = keyArea.slice(offset, offset + KEY_FORMAT_SIZE)
        if (!this.bytesEqual(current, macroKeyFormat)) {
          keyArea.set(macroKeyFormat, offset)
          changed = true
        }
      }
      if (changed) await this.writeKeyAreaSnapshot(session, keyArea)
      return found
    })
  }

  /** 完整按键区共 128 字节，按 DPI 已验证的 24 字节安全颗粒度连续寻址。 */
  private async readKeyAreaSnapshot(session: {
    readKeyArea(address: number, length: number): Promise<Uint8Array>
  }): Promise<Uint8Array> {
    const keyArea = new Uint8Array(KEY_AREA_BYTE_LENGTH)
    let address = 0
    for (const length of KEY_AREA_CHUNK_LENGTHS) {
      keyArea.set(await session.readKeyArea(address, length), address)
      address += length
    }
    return keyArea
  }

  private async writeKeyAreaSnapshot(
    session: {
      readKeyArea(address: number, length: number): Promise<Uint8Array>
      writeKeyArea(address: number, data: Uint8Array): Promise<void>
    },
    keyArea: Uint8Array,
  ): Promise<void> {
    if (keyArea.byteLength !== KEY_AREA_BYTE_LENGTH) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '当前按键区必须为 128 字节')
    }
    let address = 0
    for (const length of KEY_AREA_CHUNK_LENGTHS) {
      await session.writeKeyArea(address, keyArea.slice(address, address + length))
      address += length
    }
    const stored = await this.readKeyAreaSnapshot(session)
    this.assertStoredBytes(stored, keyArea, '按键区')
  }

  private async readMacroAreaSnapshot(session: {
    readMacroArea(address: number, length: number): Promise<Uint8Array>
  }): Promise<MacroAreaSnapshot> {
    const firstRead = await this.readMacroHeader(session)
    if (!firstRead) {
      return this.parseMacroArea(this.createEmptyMacroArea())
    }
    const { firstChunk, header } = firstRead
    const totalLength = this.readUint16(header, 2, '宏区总长度')
    if (totalLength < MACRO_HEADER_BYTE_LENGTH || totalLength > MAX_MACRO_AREA_BYTE_LENGTH) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '宏区总长度超出设备支持范围')
    }

    const raw = new Uint8Array(totalLength)
    raw.set(firstChunk.subarray(0, Math.min(firstChunk.byteLength, totalLength)))
    for (let address = firstChunk.byteLength; address < totalLength; ) {
      const length = Math.min(MACRO_TRANSFER_CHUNK_LENGTH, totalLength - address)
      raw.set(await session.readMacroArea(address, length), address)
      address += length
    }
    return this.parseMacroArea(raw)
  }

  private async readMacroHeader(session: {
    readMacroArea(address: number, length: number): Promise<Uint8Array>
  }): Promise<{ firstChunk: Uint8Array; header: Uint8Array } | undefined> {
    console.warn('[TelinkMacro] 开始读取宏区首块')
    let lastError: unknown
    // 实机对不同 LEN 的首读表现不稳定；优先按已验证的 24 字节，再尝试协议允许的最大 56 字节。
    for (const length of [MACRO_TRANSFER_CHUNK_LENGTH, MACRO_READ_FALLBACK_LENGTH]) {
      try {
        const firstChunk = await session.readMacroArea(0, length)
        if (firstChunk.byteLength < MACRO_HEADER_BYTE_LENGTH) {
          lastError = new DeviceError(DeviceErrorCode.READ_FAILED, '宏区首块长度不足 16 字节')
          continue
        }
        const header = firstChunk.slice(0, MACRO_HEADER_BYTE_LENGTH)
        // 全零可能是真空白，也可能是当前 LEN 不受支持；继续尝试下一档，避免误判为空。
        if (header.every((byte) => byte === 0)) {
          lastError = undefined
          continue
        }
        if (header[0] === 0xaa && header[1] === 0x55) {
          return { firstChunk, header }
        }
        lastError = new DeviceError(
          DeviceErrorCode.READ_FAILED,
          `宏区缺少 AA55 有效标志：${this.toHex(header)}`,
        )
      } catch (error) {
        console.warn(`[TelinkMacro] AA 首块读取失败 LEN=${length}`, error)
        lastError = error
      }
    }
    if (lastError) {
      console.warn('[TelinkMacro] AA 首块读取最终失败', lastError)
      throw lastError
    }
    console.warn('[TelinkMacro] AA 首块在 LEN=24/56 下均为全零，按空白宏区处理')
    return undefined
  }

  private async writeMacroAreaSnapshot(
    session: {
      readMacroArea(address: number, length: number): Promise<Uint8Array>
      writeMacroArea(address: number, data: Uint8Array): Promise<void>
    },
    raw: Uint8Array,
  ): Promise<void> {
    if (raw.byteLength < MACRO_HEADER_BYTE_LENGTH || raw.byteLength > MAX_MACRO_AREA_BYTE_LENGTH) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏区写入长度无效')
    }
    for (let address = 0; address < raw.byteLength; ) {
      const length = Math.min(MACRO_TRANSFER_CHUNK_LENGTH, raw.byteLength - address)
      await session.writeMacroArea(address, raw.slice(address, address + length))
      address += length
    }
    const stored = await this.readMacroAreaSnapshot(session)
    this.assertStoredBytes(stored.raw, raw, '宏区')
  }

  private parseMacroArea(raw: Uint8Array): MacroAreaSnapshot {
    if (raw.byteLength < MACRO_HEADER_BYTE_LENGTH || raw[0] !== 0xaa || raw[1] !== 0x55) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '宏区头无效')
    }
    if (this.readUint16(raw, 2, '宏区总长度') !== raw.byteLength) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '宏区总长度与读取数据不一致')
    }

    const macroCount = this.readUint16(raw, 4, '宏数量')
    const addressTableEnd = MACRO_HEADER_BYTE_LENGTH + macroCount * MACRO_ADDRESS_BYTE_LENGTH
    if (addressTableEnd > raw.byteLength) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '宏地址表超出宏区范围')
    }

    const addresses = Array.from({ length: macroCount }, (_, index) =>
      this.readUint16(
        raw,
        MACRO_HEADER_BYTE_LENGTH + index * MACRO_ADDRESS_BYTE_LENGTH,
        `宏 ${index} 地址`,
      ),
    )
    const entries: MacroAreaEntry[] = []
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      if (address === undefined) continue
      const nextAddress = index + 1 < addresses.length ? addresses[index + 1] : raw.byteLength
      if (
        nextAddress === undefined ||
        address < addressTableEnd ||
        address + MACRO_DATA_HEADER_BYTE_LENGTH > nextAddress ||
        nextAddress > raw.byteLength
      ) {
        // 宏区地址表是连续的；遇到无效尾项时，后续条目也无法可靠定位。
        // 保留此前已完整解析的宏，让“新建宏”能在下一次 AB 写入时重建并修复残留数据。
        console.warn(`[TelinkMacro] 忽略损坏的宏尾项 ${index}：地址无效`)
        break
      }
      const actionCount = this.readUint16(raw, address, `宏 ${index} 动作数量`)
      const actionEnd =
        address + MACRO_DATA_HEADER_BYTE_LENGTH + actionCount * MACRO_ACTION_BYTE_LENGTH
      if (actionEnd > nextAddress) {
        console.warn(`[TelinkMacro] 忽略损坏的宏尾项 ${index}：动作区超出范围`)
        break
      }
      entries.push({
        address,
        end: nextAddress,
        headerReserved: raw.slice(address + 2, address + MACRO_DATA_HEADER_BYTE_LENGTH),
        ...this.parseMacroActions(raw, address, actionCount),
      })
    }
    return { raw, entries }
  }

  private parseMacroActions(
    raw: Uint8Array,
    address: number,
    actionCount: number,
  ): Pick<MacroAreaEntry, 'actions' | 'hasUnmappedActions'> {
    const actions: Action[] = []
    let actionIndex = 0
    while (actionIndex < actionCount) {
      try {
        const current = this.parseMacroAction(this.getMacroActionBytes(raw, address, actionIndex))
        // 协议把 X/Y 拆成两个动作；Web contract 使用同一个坐标 Action，读取时再合并。
        if (this.isCoordinateMacroAction(current) && actionIndex + 1 < actionCount) {
          const next = this.parseMacroAction(
            this.getMacroActionBytes(raw, address, actionIndex + 1),
          )
          const merged = this.mergeCoordinateMacroActions(current, next)
          if (merged) {
            actions.push(merged)
            actionIndex += 2
            continue
          }
        }
        actions.push(current)
        actionIndex += 1
      } catch (error) {
        if (error instanceof DeviceError && error.code === DeviceErrorCode.NOT_SUPPORTED) {
          // 页面没有 MS 摆动等动作模型；保留设备原始宏，不把它伪造成可编辑的页面动作。
          return { hasUnmappedActions: true }
        }
        throw error
      }
    }
    return { actions, hasUnmappedActions: false }
  }

  private replaceMacroAreaEntry(
    snapshot: MacroAreaSnapshot,
    macro: Macro,
    index: number,
  ): Uint8Array {
    const entries: Uint8Array[] = snapshot.entries.map(
      (entry) => snapshot.raw.slice(entry.address, entry.end) as Uint8Array,
    )
    const existing = snapshot.entries[index]
    const replacement = this.serializeMacroEntry(macro, existing?.headerReserved)
    if (index === entries.length) entries.push(replacement)
    else entries[index] = replacement

    return this.buildMacroAreaRaw(entries, snapshot.raw)
  }

  /** 从宏区中移除指定索引的条目，重建地址表。 */
  private removeMacroAreaEntry(snapshot: MacroAreaSnapshot, index: number): Uint8Array {
    const entries: Uint8Array[] = snapshot.entries
      .filter((_, entryIndex) => entryIndex !== index)
      .map((entry) => snapshot.raw.slice(entry.address, entry.end) as Uint8Array)
    return this.buildMacroAreaRaw(entries, snapshot.raw)
  }

  /** 根据条目列表构建完整的宏区原始字节，保留原始总宏头中的未定义字段。 */
  private buildMacroAreaRaw(entries: Uint8Array[], originalRaw?: Uint8Array): Uint8Array {
    const totalLength =
      MACRO_HEADER_BYTE_LENGTH +
      entries.length * MACRO_ADDRESS_BYTE_LENGTH +
      entries.reduce((sum, entry) => sum + entry.byteLength, 0)
    if (totalLength > MAX_MACRO_AREA_BYTE_LENGTH) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏数据超过页面支持的 4KB 上限')
    }
    const raw = new Uint8Array(totalLength)
    // 保留原始总宏头中的未定义字段（offset 6-15），避免覆盖固件特定数据
    if (originalRaw && originalRaw.byteLength >= MACRO_HEADER_BYTE_LENGTH) {
      raw.set(originalRaw.slice(0, MACRO_HEADER_BYTE_LENGTH))
    }
    raw[0] = 0xaa
    raw[1] = 0x55
    this.writeUint16(raw, 2, totalLength)
    this.writeUint16(raw, 4, entries.length)

    let address = MACRO_HEADER_BYTE_LENGTH + entries.length * MACRO_ADDRESS_BYTE_LENGTH
    entries.forEach((entry, entryIndex) => {
      this.writeUint16(
        raw,
        MACRO_HEADER_BYTE_LENGTH + entryIndex * MACRO_ADDRESS_BYTE_LENGTH,
        address,
      )
      raw.set(entry, address)
      address += entry.byteLength
    })
    return raw
  }

  private serializeMacroEntry(macro: Macro, headerReserved?: Uint8Array): Uint8Array {
    const actions = macro.package ?? []
    const protocolActions = actions.flatMap((action) =>
      this.serializeMacroAction(action, macro.ignoreDelay === true),
    )
    if (protocolActions.length > 0xffff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏动作数量超过 uint16 上限')
    }
    const raw = new Uint8Array(
      MACRO_DATA_HEADER_BYTE_LENGTH + protocolActions.length * MACRO_ACTION_BYTE_LENGTH,
    )
    this.writeUint16(raw, 0, protocolActions.length)
    // V0105 把这两个字节定义为 reserved；编辑宏动作时必须原样保留。
    if (headerReserved) {
      raw.set(headerReserved.slice(0, 2), 2)
    }
    protocolActions.forEach((actionBytes, index) => {
      raw.set(actionBytes, MACRO_DATA_HEADER_BYTE_LENGTH + index * MACRO_ACTION_BYTE_LENGTH)
    })
    return raw
  }

  private parseMacroAction(raw: Uint8Array): Action {
    const delay = this.readUint16(raw, 0, '宏动作延时')
    const status = raw[2]
    const codeType = status & 0x7f
    const type = (status >> 7) & 1
    if (
      codeType === MACRO_ACTION_CODE_TYPE.keyboardNormal ||
      codeType === MACRO_ACTION_CODE_TYPE.keyboardHot
    ) {
      return { time: delay, type, key: raw[3] }
    }
    if (codeType === MACRO_ACTION_CODE_TYPE.mouseButton) {
      const key = PROTOCOL_MACRO_MOUSE_TO_WEB.get(raw[3])
      if (key === undefined) {
        throw new DeviceError(
          DeviceErrorCode.NOT_SUPPORTED,
          `宏包含未确认的鼠标键码 0x${raw[3].toString(16)}`,
        )
      }
      return { time: delay, type, key }
    }
    if (codeType === MACRO_ACTION_CODE_TYPE.mouseWheel) {
      const direction =
        raw[3] === MACRO_WHEEL_POSITIVE ? 1 : raw[3] === MACRO_WHEEL_NEGATIVE ? -1 : undefined
      if (direction === undefined) {
        throw new DeviceError(
          DeviceErrorCode.NOT_SUPPORTED,
          `宏包含未确认的滚轮方向 0x${raw[3].toString(16)}`,
        )
      }
      return { time: delay, type: 0, key: WEB_WHEEL_KEY, vWheel: direction, hWheel: 0 }
    }
    if (codeType === MACRO_ACTION_CODE_TYPE.mouseX || codeType === MACRO_ACTION_CODE_TYPE.mouseY) {
      const direction = (status & 0x80) === 0 ? 1 : -1
      const magnitude = raw[3]
      const signedMagnitude = direction * magnitude
      return {
        time: delay,
        type: 0,
        key: WEB_COORDINATE_KEY,
        x: codeType === MACRO_ACTION_CODE_TYPE.mouseX ? signedMagnitude : 0,
        y: codeType === MACRO_ACTION_CODE_TYPE.mouseY ? signedMagnitude : 0,
      }
    }
    if (codeType === MACRO_ACTION_CODE_TYPE.mouseSwipe) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        `宏包含未映射的 MS 摆动动作（Code=0x${raw[3].toString(16)}）`,
      )
    }
    throw new DeviceError(
      DeviceErrorCode.NOT_SUPPORTED,
      `宏包含页面尚未映射的动作类型 0x${codeType.toString(16)}`,
    )
  }

  private serializeMacroAction(action: Action, ignoreDelay: boolean): Uint8Array[] {
    if (!action) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏动作不能为空')
    }
    const delay = ignoreDelay ? 2 : this.requireUint16(action.time, '宏动作延时')
    if (action.key === WEB_COORDINATE_KEY) {
      return this.serializeCoordinateMacroAction(action, delay)
    }
    if (action.key === WEB_WHEEL_KEY) {
      return [this.serializeWheelMacroAction(action, delay)]
    }
    if (!Number.isInteger(action.type) || (action.type !== 0 && action.type !== 1)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏动作类型必须为按下或抬起')
    }
    if (!Number.isInteger(action.key) || action.key < 0 || action.key > 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏动作键码必须是 uint8')
    }
    if (
      action.x !== undefined ||
      action.y !== undefined ||
      action.hWheel !== undefined ||
      action.vWheel !== undefined ||
      action.mouseKey !== undefined
    ) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, 'VT2 ULTRA V2 宏暂不支持该动作附加字段')
    }

    const mouseCode = WEB_MACRO_MOUSE_TO_PROTOCOL.get(action.key)
    const codeType =
      mouseCode === undefined
        ? action.key === 0x02
          ? MACRO_ACTION_CODE_TYPE.keyboardHot
          : MACRO_ACTION_CODE_TYPE.keyboardNormal
        : MACRO_ACTION_CODE_TYPE.mouseButton
    const code = mouseCode ?? action.key
    return [new Uint8Array([delay & 0xff, delay >> 8, (action.type << 7) | codeType, code])]
  }

  private serializeCoordinateMacroAction(action: Action, delay: number): Uint8Array[] {
    const x = action.x ?? 0
    const y = action.y ?? 0
    if (!Number.isInteger(x) || !Number.isInteger(y)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏坐标偏移必须是整数')
    }
    return [
      this.serializeCoordinateAxis(MACRO_ACTION_CODE_TYPE.mouseX, x, delay, 'X'),
      this.serializeCoordinateAxis(MACRO_ACTION_CODE_TYPE.mouseY, y, delay, 'Y'),
    ]
  }

  private serializeCoordinateAxis(
    codeType: typeof MACRO_ACTION_CODE_TYPE.mouseX | typeof MACRO_ACTION_CODE_TYPE.mouseY,
    value: number,
    delay: number,
    axis: string,
  ): Uint8Array {
    const magnitude = Math.abs(value)
    if (magnitude > MACRO_COORDINATE_MAX_MAGNITUDE) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        `宏坐标${axis}偏移 ${value} 超过协议支持范围 -255~255`,
      )
    }
    const status = (value < 0 ? 0x80 : 0) | codeType
    return new Uint8Array([delay & 0xff, delay >> 8, status, magnitude])
  }

  private serializeWheelMacroAction(action: Action, delay: number): Uint8Array {
    if (action.hWheel !== undefined && action.hWheel !== 0) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, 'VT2 ULTRA V2 宏仅确认支持垂直滚轮动作')
    }
    const direction = action.vWheel ?? 0
    if (direction !== -1 && direction !== 1) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏滚轮方向必须是 -1 或 1')
    }
    const code = direction === 1 ? MACRO_WHEEL_POSITIVE : MACRO_WHEEL_NEGATIVE
    return new Uint8Array([delay & 0xff, delay >> 8, MACRO_ACTION_CODE_TYPE.mouseWheel, code])
  }

  private isCoordinateMacroAction(action: Action): boolean {
    return action.key === WEB_COORDINATE_KEY && action.type === 0
  }

  private mergeCoordinateMacroActions(first: Action, second: Action): Action | undefined {
    if (!this.isCoordinateMacroAction(first) || !this.isCoordinateMacroAction(second)) {
      return undefined
    }
    if (first.time !== second.time) return undefined
    const firstX = first.x ?? 0
    const firstY = first.y ?? 0
    const secondX = second.x ?? 0
    const secondY = second.y ?? 0
    if (firstX !== 0 && secondX !== 0) return undefined
    if (firstY !== 0 && secondY !== 0) return undefined
    return {
      key: WEB_COORDINATE_KEY,
      type: 0,
      time: first.time,
      x: firstX || secondX,
      y: firstY || secondY,
    }
  }

  private getMacroActionBytes(raw: Uint8Array, address: number, actionIndex: number): Uint8Array {
    const actionOffset =
      address + MACRO_DATA_HEADER_BYTE_LENGTH + actionIndex * MACRO_ACTION_BYTE_LENGTH
    return raw.slice(actionOffset, actionOffset + MACRO_ACTION_BYTE_LENGTH)
  }

  private createMacroSummary(index: number): Macro {
    return { index, name: this.resolveMacroName(index) }
  }

  /** 恢复出厂后由 device 调用：名称区是否被固件重置未在协议中说明，下次读取时以设备为准。 */
  public invalidateMacroNameArea(): void {
    this.macroNameArea = undefined
  }

  /**
   * 名称来自设备名称区；槽位未命名或索引没有名称槽位时回退 `Macro{n}`。
   */
  private resolveMacroName(index: number): string {
    const defaultName = `Macro${index + 1}`
    if (this.macroNameArea && hasVisionApMacroNameSlot(index)) {
      const offset = getVisionApMacroNameSlotOffset(index)
      return (
        decodeVisionApMacroName(
          this.macroNameArea.subarray(
            offset,
            offset + VISION_AP_MOUSE_MACRO_NAME_AREA.slotByteLength,
          ),
        ) ?? defaultName
      )
    }
    return defaultName
  }

  /**
   * 读取宏名称区并缓存在会话内；同时到达的读取合并为一次 B9 传输。
   */
  private async loadMacroNameArea(): Promise<Uint8Array> {
    if (this.macroNameArea) return this.macroNameArea
    if (!this.macroNameAreaInFlight) {
      this.macroNameAreaInFlight = this.telinkDevice
        .runMacroNameAreaSession((session) => session.readMacroNameArea())
        .then((area) => {
          this.macroNameArea = area
          return area
        })
        .finally(() => {
          this.macroNameAreaInFlight = undefined
        })
    }
    return this.macroNameAreaInFlight
  }

  /**
   * 将宏名称写入 V0106 名称区：只替换目标 16 字节槽位，整区 240 字节回写并回读确认。
   * 名称与设备上已存内容一致时直接返回，避免每次保存动作都产生 20 包名称区传输。
   */
  private async persistMacroName(index: number, name: string): Promise<void> {
    if (!hasVisionApMacroNameSlot(index)) return
    const area = await this.loadMacroNameArea()
    const slot = encodeVisionApMacroName(name)
    const offset = getVisionApMacroNameSlotOffset(index)
    if (this.bytesEqual(area.subarray(offset, offset + slot.byteLength), slot)) return
    const next = area.slice()
    next.set(slot, offset)
    await this.writeMacroNameArea(next)
  }

  /** 删除宏后名称槽位整体前移一位，末槽清空；与宏区条目索引前移保持一致。 */
  private async removeMacroName(index: number): Promise<void> {
    if (!hasVisionApMacroNameSlot(index)) return
    const area = await this.loadMacroNameArea()
    const next = area.slice()
    const offset = getVisionApMacroNameSlotOffset(index)
    next.copyWithin(offset, offset + VISION_AP_MOUSE_MACRO_NAME_AREA.slotByteLength)
    next.fill(0, next.byteLength - VISION_AP_MOUSE_MACRO_NAME_AREA.slotByteLength)
    if (this.bytesEqual(next, area)) return
    await this.writeMacroNameArea(next)
  }

  private async writeMacroNameArea(next: Uint8Array): Promise<void> {
    try {
      this.macroNameArea = await this.telinkDevice.runMacroNameAreaSession(async (session) => {
        await session.writeMacroNameArea(next)
        const readBack = await session.readMacroNameArea()
        this.assertStoredBytes(readBack, next, '宏名称区')
        return readBack
      })
    } catch (error) {
      // 写入或回读失败后设备上的名称区状态未知，丢弃快照，下次读取时重新回读。
      this.macroNameArea = undefined
      throw error
    }
  }

  /**
   * V0105 没有宏级播放字段。为兼容 Web 的 Macro.loop，首次回显从当前 profile
   * 已绑定该宏的按键 KeyFormat 推导；没有绑定时按普通一次播放处理。
   */
  private async readMacroLoopFromCurrentKeyArea(macroIndex: number): Promise<number> {
    const keyArea = await this.telinkDevice.runKeyAreaSession((session) =>
      this.readKeyAreaSnapshot(session),
    )
    for (let slot = 0; slot * KEY_FORMAT_SIZE < keyArea.byteLength; slot += 1) {
      const attr = this.parseKeyFormat(
        keyArea.slice(slot * KEY_FORMAT_SIZE, (slot + 1) * KEY_FORMAT_SIZE),
      )
      if (attr.type === KEY_ATTR_TYPE.macro && attr.macro?.index === macroIndex) {
        return this.requireMacroLoop(attr.macro.loop ?? 1)
      }
    }
    return 1
  }

  /** KeyFormat 已成功写入后，播放方式以当前 profile 的键位区为准，不再保留待绑定偏好。 */
  private clearPendingMacroLoopPreference(attr: keyAttr | undefined): void {
    if (attr?.type !== KEY_ATTR_TYPE.macro || !attr.macro) return
    this.pendingMacroLoopPreferenceCache.delete(attr.macro.index)
  }

  private createEmptyMacroArea(): Uint8Array {
    const raw = new Uint8Array(MACRO_HEADER_BYTE_LENGTH)
    raw[0] = 0xaa
    raw[1] = 0x55
    this.writeUint16(raw, 2, MACRO_HEADER_BYTE_LENGTH)
    return raw
  }

  private assertMacroIndex(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index > 0xffff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '宏索引必须是 uint16')
    }
    // TODO(V0106): 名称区 / 多宏示例均为 15 个宏（FACT-V0106-MACRO-NAME-LAYOUT，
    // PDF §2.2.14–§2.2.15「新建 15 宏」，SDK 索引 0-14）。当前仍按 uint16 放行，尚未按此上限拒绝。
  }

  private readUint16(raw: Uint8Array, offset: number, field: string): number {
    if (offset < 0 || offset + 1 >= raw.byteLength) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, `${field}读取越界`)
    }
    return raw[offset] | (raw[offset + 1] << 8)
  }

  private writeUint16(raw: Uint8Array, offset: number, value: number): void {
    raw[offset] = value & 0xff
    raw[offset + 1] = value >> 8
  }

  private requireUint16(value: number, field: string): number {
    if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${field}必须是 uint16`)
    }
    return value
  }

  private requireMacroLoop(value: number): number {
    if (!Number.isInteger(value) || value < 1 || value > 0xffff) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        '宏循环次数/播放方式必须为 1-65535 的整数',
      )
    }
    return value
  }

  private bytesEqual(actual: Uint8Array, expected: Uint8Array): boolean {
    return (
      actual.byteLength === expected.byteLength &&
      actual.every((value, index) => value === expected[index])
    )
  }

  /** ACK 只表示命令被接收；重新读取设备数据，确认本次内容确实已经保存。 */
  private assertStoredBytes(actual: Uint8Array, expected: Uint8Array, areaName: string): void {
    if (this.bytesEqual(actual, expected)) return
    const compareLength = Math.min(actual.byteLength, expected.byteLength)
    let mismatchOffset = compareLength
    for (let offset = 0; offset < compareLength; offset += 1) {
      if (actual[offset] !== expected[offset]) {
        mismatchOffset = offset
        break
      }
    }
    throw new DeviceError(
      DeviceErrorCode.WRITE_FAILED,
      `${areaName}写入后回读不一致（offset=${mismatchOffset}，期望长度=${expected.byteLength}，实际长度=${actual.byteLength}）`,
    )
  }

  private toHex(raw: Uint8Array): string {
    return Array.from(raw, (byte) => byte.toString(16).padStart(2, '0')).join(' ')
  }

  private resolveKeySlot(index: unknown): number {
    if (typeof index !== 'number' || !Number.isInteger(index)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '按键索引必须是整数')
    }
    const slot = KEY_SLOT_BY_LAYOUT_INDEX.get(index)
    if (slot === undefined) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `VT2 ULTRA V2 不支持按键索引 ${index}`)
    }
    return slot
  }

  private parseKeyFormat(data: Uint8Array): keyAttr {
    if (data.byteLength !== KEY_FORMAT_SIZE) return { type: KEY_ATTR_TYPE.default }

    switch (data[0]) {
      case 0x01:
        // 厂商确认：regular key 的 Code=0 表示禁用，不能误解为未知鼠标键。
        return data[1] === 0 ? { type: KEY_ATTR_TYPE.disable } : this.parseMouseButton(data[1])
      case 0x02:
        return {
          type: KEY_ATTR_TYPE.horizontalWheel,
          wheel: { type: 0xff, valueType: 0xff, direction: data[2] === 0xff ? 1 : 0 },
        }
      case 0x03:
        return {
          type: KEY_ATTR_TYPE.verticalWheel,
          wheel: { type: 0xff, valueType: 0xff, direction: data[2] === 0xff ? 1 : 0 },
        }
      case 0x04:
        return this.parseDpiKey(data[1])
      case 0x05:
        return {
          type: KEY_ATTR_TYPE.function,
          snipingFire: { type: 2, fireType: data[2] === 2 ? 0 : 1 },
        }
      case REPORT_RATE_KEY_FORMAT_TYPE:
        // VT2 ULTRA V2 的物理 DIY 键出厂值为 `06 00 00 00`。
        // 其它 Code1 表示指定回报率，现有 contract 无法无损表达，因此仍按 default 处理。
        return data[1] === 0 && data[2] === 0 && data[3] === 0
          ? { type: KEY_ATTR_TYPE.diy, diy: { type: REPORT_RATE_DIY_TYPE } }
          : { type: KEY_ATTR_TYPE.default }
      case 0x07:
        return data[2] === 0 && data[3] === 0
          ? { type: KEY_ATTR_TYPE.keyboard, code: [data[1]] }
          : {
              type: KEY_ATTR_TYPE.arbitraryCombined,
              code: [data[1], data[2], data[3]].filter((code) => code !== 0),
            }
      case 0x09:
        return { type: KEY_ATTR_TYPE.media, code: [data[1] | (data[2] << 8)] }
      case 0x0a:
        return { type: KEY_ATTR_TYPE.media, code: [data[1] | (data[2] << 8)] }
      case 0x0b: {
        // 0x0B Macro keys：code1=宏编号，code2=Stop mode（0=普通一次播放、1=抬起时停止、2=按下时停止）
        const stopMode = data[2]
        const loop = stopMode === 1 ? 0xffff : stopMode === 2 ? 0xfffe : 1
        return { type: KEY_ATTR_TYPE.macro, macro: { index: data[1], loop } }
      }
      case 0x0c: {
        // 0x0C：code1=宏编号，code2/code3 分别为播放次数的高/低 8 位。
        const plays = (data[2] << 8) | data[3]
        return { type: KEY_ATTR_TYPE.macro, macro: { index: data[1], loop: plays > 0 ? plays : 1 } }
      }
      case 0x0d:
        return {
          type: KEY_ATTR_TYPE.combined,
          code: [data[2], data[3]],
          combinKey: this.parseModifierBits(data[1]),
        }
      case 0x0e:
        return { type: KEY_ATTR_TYPE.function, snipingFire: { type: 1 } }
      case RECEIVER_SWITCH_KEY_FORMAT_TYPE:
        return { type: KEY_ATTR_TYPE.diy, diy: { type: RECEIVER_SWITCH_DIY_TYPE } }
      case CONFIG_CYCLE_KEY_FORMAT_TYPE:
        return data[1] === CONFIG_CYCLE_PROTOCOL_CODE
          ? { type: KEY_ATTR_TYPE.diy, diy: { type: CONFIG_CYCLE_DIY_TYPE } }
          : { type: KEY_ATTR_TYPE.default }
      default:
        // 当前 Web contract 无法无损表达 Fire、Repeat、Swipe 等类型，避免错误回写。
        return { type: KEY_ATTR_TYPE.default }
    }
  }

  private parseMouseButton(protocolCode: number): keyAttr {
    const webIndex = PROTOCOL_MOUSE_TO_WEB_INDEX.get(protocolCode)
    if (webIndex === undefined) return { type: KEY_ATTR_TYPE.default }
    const keys: mouseKey = [0, 0, 0, 0, 0]
    keys[webIndex] = 1
    return { type: KEY_ATTR_TYPE.mouseButton, mouseKey: { mouseKey: keys, isDouble: false } }
  }

  private parseDpiKey(protocolCode: number): keyAttr {
    const type = PROTOCOL_DPI_TO_WEB_TYPE.get(protocolCode)
    return type === undefined
      ? { type: KEY_ATTR_TYPE.default }
      : { type: KEY_ATTR_TYPE.dpi, dpi: { type } }
  }

  private parseModifierBits(
    bits: number,
  ): [number, number, number, number, number, number, number, number] {
    return [
      bits & 1,
      (bits >> 1) & 1,
      (bits >> 2) & 1,
      (bits >> 3) & 1,
      (bits >> 4) & 1,
      (bits >> 5) & 1,
      (bits >> 6) & 1,
      (bits >> 7) & 1,
    ]
  }

  private serializeKeyFormat(attr: keyAttr | undefined): Uint8Array {
    if (!attr || !Number.isInteger(attr.type)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '按键定义缺少有效类型')
    }

    const data = new Uint8Array(KEY_FORMAT_SIZE)
    switch (attr.type) {
      case KEY_ATTR_TYPE.keyboard:
        data[0] = 0x07
        data[1] = this.requireByte(attr.code?.[0], '键盘键码')
        return data
      case KEY_ATTR_TYPE.combined:
        data[0] = 0x0d
        data[1] = this.serializeModifierBits(attr.combinKey)
        data.set(this.serializeHotKeyCodes(attr.code), 2)
        return data
      case KEY_ATTR_TYPE.mouseButton:
        data[0] = 0x01
        data[1] = this.serializeMouseButton(attr.mouseKey)
        return data
      case KEY_ATTR_TYPE.media:
        return this.serializeMediaKey(attr.code?.[0])
      case KEY_ATTR_TYPE.macro:
        return this.serializeMacroKey(attr.macro)
      case KEY_ATTR_TYPE.disable:
        return new Uint8Array([0x01, 0, 0, 0])
      case KEY_ATTR_TYPE.dpi:
        data[0] = 0x04
        const protocolDpiType = attr.dpi && WEB_DPI_TO_PROTOCOL_TYPE.get(attr.dpi.type)
        if (protocolDpiType === undefined) {
          throw new DeviceError(
            DeviceErrorCode.NOT_SUPPORTED,
            '该设备仅确认支持 DPI 加、减、DPI+ 循环和 DPI- 循环改键',
          )
        }
        data[1] = protocolDpiType
        return data
      case KEY_ATTR_TYPE.function:
        return this.serializeFireKey(attr)
      case KEY_ATTR_TYPE.diy:
        if (attr.diy?.type === REPORT_RATE_DIY_TYPE) {
          return new Uint8Array([REPORT_RATE_KEY_FORMAT_TYPE, 0, 0, 0])
        }
        if (attr.diy?.type === CONFIG_CYCLE_DIY_TYPE) {
          return new Uint8Array([CONFIG_CYCLE_KEY_FORMAT_TYPE, CONFIG_CYCLE_PROTOCOL_CODE, 0, 0])
        }
        if (attr.diy?.type === RECEIVER_SWITCH_DIY_TYPE) {
          return new Uint8Array([RECEIVER_SWITCH_KEY_FORMAT_TYPE, 0, 0, 0])
        }
        throw new DeviceError(
          DeviceErrorCode.NOT_SUPPORTED,
          'VT2 ULTRA V2 仅确认支持回报率、配置循环和接收器切换 DIY 功能',
        )
      case KEY_ATTR_TYPE.verticalWheel:
        data[0] = 0x03
        data[1] = WHEEL_CODE_L
        data[2] = attr.wheel?.direction === 1 ? 0xff : 0x01
        return data
      case KEY_ATTR_TYPE.horizontalWheel:
        data[0] = 0x02
        data[1] = WHEEL_CODE_L
        data[2] = attr.wheel?.direction === 1 ? 0xff : 0x01
        return data
      case KEY_ATTR_TYPE.arbitraryCombined:
        return this.serializeBasicKeyCombination(attr.code)
      default:
        throw new DeviceError(
          DeviceErrorCode.NOT_SUPPORTED,
          `VT2 ULTRA V2 暂不支持改键类型 0x${attr.type.toString(16)}`,
        )
    }
  }

  private hasReceiverSwitchKey(keyArea: Uint8Array): boolean {
    for (
      let offset = 0;
      offset + KEY_FORMAT_SIZE <= keyArea.byteLength;
      offset += KEY_FORMAT_SIZE
    ) {
      if (keyArea[offset] === RECEIVER_SWITCH_KEY_FORMAT_TYPE) return true
    }
    return false
  }

  private async writeReceiverSwitchFlag(
    value: 0 | 1,
    profileIndex = this.telinkDevice.currentConfig.index,
  ): Promise<void> {
    await this.telinkDevice.runFunctionAreaSession(async (session) => {
      const area = await session.readFunctionAreaSnapshot()
      area[RECEIVER_SWITCH_OFFSET] = value
      await session.writeFunctionAreaSnapshot(area)
    }, profileIndex)
  }

  private serializeFireKey(attr: keyAttr): Uint8Array {
    if (attr.snipingFire?.type === 1) {
      return new Uint8Array([0x0e, 0, 0, 0])
    }
    if (attr.snipingFire?.type !== 2) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        'VT2 ULTRA V2 仅支持狙击键和火力键，不支持压枪宏选择',
      )
    }

    return new Uint8Array([0x05, FIRE_DEFAULT_INTERVAL_MS, FIRE_DEFAULT_SHOT_COUNT, 0])
  }

  /**
   * 按协议 KeyFormat 的 Macro keys 定义编码宏按键：
   * - 0x0B：Macro number + Stop mode（0=普通一次播放、1=抬起时停止、2=按下时停止）
   * - 0x0C：Macro number + Number of plays（高 8 位 + 低 8 位）
   * Web 的 loop 值：0xFFFE=指派按键切换、0xFFFF=按下时播放、1-0xFFFD=指定次数。
   */
  private serializeMacroKey(macroAttr?: macro): Uint8Array {
    const index = this.requireByte(macroAttr?.index, '宏索引')
    const loop = this.requireMacroLoop(macroAttr?.loop ?? 1)
    if (loop === 0xfffe) {
      // 指派按键切换 → 0x0B Stop mode 2（再次按下停止）
      return new Uint8Array([0x0b, index, 2, 0])
    }
    if (loop === 0xffff) {
      // 按下时播放 → 0x0B Stop mode 1（抬起时停止）
      return new Uint8Array([0x0b, index, 1, 0])
    }
    if (loop === 1) {
      // 普通一次播放 → 0x0B Stop mode 0
      return new Uint8Array([0x0b, index, 0, 0])
    }
    if (loop >= 2 && loop <= 0xfffd) {
      // 指定次数 → 0x0C Number of plays
      return new Uint8Array([0x0c, index, loop >> 8, loop & 0xff])
    }
    throw new DeviceError(
      DeviceErrorCode.NOT_SUPPORTED,
      `宏循环次数 ${loop} 超出协议两字节范围（2-65533）`,
    )
  }

  private serializeMediaKey(code: number | undefined): Uint8Array {
    if (typeof code !== 'number' || !Number.isInteger(code) || code < 0 || code > 0xffff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '多媒体键码必须是 uint16')
    }
    const keyFormatType = SYSTEM_CONTROL_CODES.has(code) ? 0x0a : 0x09
    return new Uint8Array([keyFormatType, code & 0xff, code >> 8, 0])
  }

  private serializeBasicKeyCombination(code: number[] | undefined): Uint8Array {
    if (!Array.isArray(code) || code.length < 2 || code.length > 3) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '无修饰键组合必须包含 2 至 3 个键盘键码')
    }
    const data = new Uint8Array(KEY_FORMAT_SIZE)
    data[0] = 0x07
    code.forEach((value, index) => {
      data[index + 1] = this.requireByte(value, `无修饰键组合第 ${index + 1} 个键码`)
    })
    return data
  }

  private serializeHotKeyCodes(code: number[] | undefined): Uint8Array {
    if (!Array.isArray(code) || code.length < 1 || code.length > 2) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        '带修饰键组合最多支持 2 个普通键；当前协议 KeyFormat 没有第三个主键字节',
      )
    }
    return new Uint8Array([
      this.requireByte(code[0], '组合键主键码'),
      code.length === 2 ? this.requireByte(code[1], '组合键附加键码') : 0,
    ])
  }

  private serializeMouseButton(value: keyAttr['mouseKey']): number {
    if (!value || value.isDouble) {
      throw new DeviceError(DeviceErrorCode.NOT_SUPPORTED, '该设备未确认双击鼠标键改键')
    }
    const selected = value.mouseKey
      .map((enabled, index) => (enabled === 1 ? index : -1))
      .filter((index) => index >= 0)
    if (selected.length !== 1) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '鼠标按键改键必须且只能选择一个目标')
    }
    const protocolCode = WEB_MOUSE_TO_PROTOCOL.get(selected[0])
    if (protocolCode === undefined) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '鼠标按键目标无效')
    }
    return protocolCode
  }

  private serializeModifierBits(value: keyAttr['combinKey']): number {
    if (!value || value.length !== 8 || value.some((bit) => bit !== 0 && bit !== 1)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '组合键修饰键必须是 8 位 0/1 数组')
    }
    return value.reduce((bits, enabled, index) => bits | (enabled << index), 0)
  }

  private requireByte(value: number | undefined, name: string): number {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 0xff) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${name}必须是 uint8`)
    }
    return value
  }
}
